// useAppUpdate — actualización automática de la app tras cada deploy (D77).
//
// Problema que resuelve: con deploys casi diarios, los usuarios quedaban
// viendo la versión ANTERIOR de la plataforma hasta hacer hard refresh
// (Cmd+Shift+R), porque el service worker de la PWA seguía sirviendo el
// precache viejo y nadie le preguntaba al servidor si había versión nueva
// mientras la pestaña estaba abierta.
//
// Diseño (UX híbrida, decisión CEO 2026-07-13):
//  - Detección: `registration.update()` cada 60s + al recuperar foco o
//    visibilidad de la pestaña (throttle 30s). Aplica igual en navegador
//    normal y en PWA instalada (standalone).
//  - Aplicación segura: cuando hay versión nueva esperando (`onNeedRefresh`),
//    se activa en el PRÓXIMO cambio de ruta del router — un momento en que el
//    usuario ya decidió moverse de pantalla, así nunca se recarga a mitad de
//    un formulario.
//  - Fallback visible: si el usuario no navega en 5 minutos, aparece un toast
//    discreto "Nueva versión disponible — Actualizar" (UpdateToast.vue).
//
// Por qué `registerType: 'prompt'` y no 'autoUpdate' (vite.config.ts):
// con 'autoUpdate' el SW nuevo hace skipWaiting apenas se instala y
// `cleanupOutdatedCaches` borra el precache viejo EN ESE INSTANTE — chunks
// lazy de la versión vieja aún no cargados quedan inalcanzables en plena
// sesión (incidente real 2026-07-09, occurrence_count=26). Con 'prompt' el SW
// viejo sigue controlando con su precache intacto (la versión vieja permanece
// 100% funcional, incluso offline) hasta que ESTE módulo decide activar la
// nueva posteando SKIP_WAITING en un momento seguro.
import { readonly, ref } from 'vue';
import { START_LOCATION, type Router } from 'vue-router';
import { trackError } from '@/utils/error-tracking';

const CHECK_INTERVAL_MS = 60_000;
// Throttle de los checks disparados por focus/visibilitychange (evita
// ráfagas al alternar ventanas).
const MIN_CHECK_GAP_MS = 30_000;
const TOAST_DELAY_MS = 5 * 60_000;
// Toast más corto cuando el update lo activó OTRA pestaña: este tab ya corre
// código viejo con el precache viejo limpiado — conviene recargar pronto.
const OTHER_TAB_TOAST_DELAY_MS = 30_000;
// Guard anti-loop con TTL (NO flag permanente: con deploys diarios una sesión
// larga puede ver 2+ updates legítimos el mismo día).
const RELOAD_GUARD_KEY = 'wiservet:app-update-reload';
const RELOAD_GUARD_TTL_MS = 2 * 60_000;
// Espera máxima a que el SW nuevo tome control antes de recargar igual.
// Mismo perfil que el fix de vite:preloadError (cto-reviewer PR #109: el
// precache tiene ~130 entries, 2s se quedaba corto en conexión lenta).
const SW_CONTROL_TIMEOUT_MS = 4_500;

type ApplyVia = 'route-change' | 'toast' | 'chunk-error' | 'chunk-error-router';

// Estado compartido entre todos los consumidores (singleton de módulo,
// mismo patrón que usePwaInstall.ts).
const updateAvailable = ref(false);
const toastVisible = ref(false);
let applying = false;
let initialized = false;
let toastTimer: number | undefined;

export function useAppUpdate() {
  return {
    updateAvailable: readonly(updateAvailable),
    toastVisible: readonly(toastVisible),
    applyFromToast: () => applyUpdate('toast'),
    dismissToast: () => {
      // Ocultar el toast NO cancela el update: sigue aplicándose en la
      // próxima navegación.
      toastVisible.value = false;
    },
  };
}

function reloadGuardActive(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    return Number.isFinite(at) && Date.now() - at < RELOAD_GUARD_TTL_MS;
  } catch {
    return false;
  }
}

function armReloadGuard(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage disabled (modo privado estricto) — el TTL guard no
    // funciona, pero `applying` sigue cortando reentradas dentro del tab.
  }
}

function scheduleToast(delay: number): void {
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (updateAvailable.value && !applying) toastVisible.value = true;
  }, delay);
}

/**
 * Espera a que el SW nuevo tome control Y termine de activarse, con timeout
 * de seguridad. El segundo paso importa: `clients.claim()` corre DENTRO del
 * evento activate — `controllerchange` llega cuando el SW nuevo todavía está
 * `activating`, y una navegación despachada en ese instante puede ser servida
 * por el SW viejo desde el precache compartido (race observada en E2E
 * 2026-07-13: la página aterrizaba una versión atrás). Esperar el estado
 * `activated` del controller cierra esa ventana.
 */
function waitForController(timeoutMs = SW_CONTROL_TIMEOUT_MS): Promise<'controllerchange' | 'timeout'> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (via: 'controllerchange' | 'timeout') => {
      if (done) return;
      done = true;
      resolve(via);
    };
    const timer = window.setTimeout(() => finish('timeout'), timeoutMs);
    const settle = () => {
      const controller = navigator.serviceWorker?.controller;
      if (!controller || controller.state === 'activated') {
        window.clearTimeout(timer);
        finish('controllerchange');
        return;
      }
      controller.addEventListener('statechange', () => {
        if (controller.state === 'activated') {
          window.clearTimeout(timer);
          finish('controllerchange');
        }
      });
    };
    navigator.serviceWorker?.addEventListener('controllerchange', settle, { once: true });
  });
}

/**
 * Activa la versión nueva (SKIP_WAITING al SW en espera) y recarga.
 * `targetPath` permite aterrizar directo en la ruta que el usuario quería
 * (caso route-change: la navegación SPA se canceló y se repite full-load).
 */
export async function applyUpdate(via: ApplyVia, targetPath?: string): Promise<void> {
  if (applying) return;
  applying = true;
  toastVisible.value = false;
  armReloadGuard();

  // Observabilidad: fila en app_events (severity low) para confirmar en
  // /admin/eventos que el flujo funciona en prod. keepalive:true en
  // error-tracking hace que el POST sobreviva al reload.
  // Los títulos de recuperación automática de este archivo los clasifica el
  // backend como INFORMATIVOS (se guardan cerrados, una fila por aviso; ver
  // informativeRecoveryPrefixes en events_handler.go). Si cambias un título,
  // actualiza esa lista.
  trackError(`PWA update aplicado (via=${via})`, {
    source: 'manual',
    severity: 'low',
    via,
    targetPath,
    build: typeof __BUILD_TS__ !== 'undefined' ? __BUILD_TS__ : 'unknown',
  });

  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg?.waiting) {
      const target = reg.waiting;
      const controlled = waitForController();
      // El sw.js generado (workbox generateSW) escucha este mensaje y llama
      // self.skipWaiting() → clientsClaim toma control → controllerchange.
      target.postMessage({ type: 'SKIP_WAITING' });
      await controlled;
      // Cinturón extra contra la race de ruteo de navegación (E2E 2026-07-13:
      // aún con el controller nuevo `activated`, una navegación inmediata
      // podía ser servida por el SW saliente desde el precache compartido —
      // se aterrizaba una versión atrás). Esperar a que el worker promovido
      // sea EL active de la registración + settle corto antes de navegar.
      for (let i = 0; i < 10 && reg.active !== target; i++) {
        await new Promise((resolve) => window.setTimeout(resolve, 150));
      }
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    }
  } catch {
    // Sin registration accesible: recargar igual — peor caso, el usuario
    // sigue en la versión vieja y el guard TTL evita loops.
  }

  if (targetPath) window.location.assign(targetPath);
  else window.location.reload();
}

/**
 * Recuperación cuando un chunk con hash viejo falla (deploy pisó el dist).
 * Reemplaza la lógica inline previa de `vite:preloadError` en main.ts, que
 * asumía `autoUpdate` (skipWaiting automático): bajo 'prompt' el
 * `controllerchange` NUNCA llega solo — hay que postear SKIP_WAITING al SW
 * waiting explícitamente o el reload volvería a caer en el precache viejo.
 */
export async function recoverFromChunkError(
  via: 'chunk-error' | 'chunk-error-router',
  targetPath?: string,
): Promise<void> {
  const hadController = 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
  if (!hadController) {
    trackError(`vite:preloadError — recuperado con reload (via=${via})`, {
      source: 'manual',
      via: 'no-sw-controller',
      hadController,
    });
    window.location.reload();
    return;
  }

  const controlled = waitForController();
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Todavía no hay SW nuevo instalado: pedir el check y dejar que el
      // ciclo install→waiting→SKIP_WAITING corra solo (el timeout cubre si
      // no llega a tiempo).
      await reg?.update();
      const again = await navigator.serviceWorker.getRegistration();
      again?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {
    // Seguimos al timeout.
  }
  const how = await controlled;
  trackError(`vite:preloadError — recuperado con reload (via=${via})`, {
    source: 'manual',
    via: how,
    hadController,
  });
  if (targetPath) window.location.assign(targetPath);
  else window.location.reload();
}

const CONSISTENCY_GUARD_KEY = 'wiservet:consistency-reload';
const CONSISTENCY_GUARD_TTL_MS = 30_000;

/**
 * Compara el bundle principal que corre esta página contra el que referencia
 * el index.html del precache del SW controlador. Si difieren, la página
 * aterrizó una versión atrás durante una transición de SW → reload único.
 */
async function verifyLandedVersion(): Promise<void> {
  try {
    if (!navigator.serviceWorker.controller) return;
    const running = document
      .querySelector('script[src^="/assets/index-"]')
      ?.getAttribute('src')
      ?.replace(/^\//, '');
    if (!running) return;
    const cacheName = `workbox-precache-v2-${window.location.origin}/`;
    const cache = await caches.open(cacheName);
    const keys = await cache.keys();
    const indexKey = keys.find((r) => r.url.startsWith(`${window.location.origin}/index.html?`));
    if (!indexKey) return;
    const response = await cache.match(indexKey);
    if (!response) return;
    const expected = (await response.text()).match(/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0];
    if (!expected || expected === running) return;

    const at = Number(sessionStorage.getItem(CONSISTENCY_GUARD_KEY) ?? 0);
    if (Number.isFinite(at) && Date.now() - at < CONSISTENCY_GUARD_TTL_MS) return;
    sessionStorage.setItem(CONSISTENCY_GUARD_KEY, String(Date.now()));
    trackError('PWA update — página aterrizó una versión atrás, reload de consistencia', {
      source: 'manual',
      severity: 'low',
      running,
      expected,
    });
    window.location.reload();
  } catch {
    // CacheStorage bloqueado o cache inexistente: sin chequeo, las redes de
    // seguridad (preloadError/router.onError) siguen cubriendo.
  }
}

/**
 * Registrar el SW + update checks + hook de navegación. Llamar una vez en
 * main.ts (idempotente).
 *
 * El registro es VANILLA (navigator.serviceWorker.register) a propósito, NO
 * el helper `registerSW` de `virtual:pwa-register`: ese helper, en modo
 * prompt, agrega internamente un listener `controlling → location.reload()`
 * que fuerza recarga INSTANTÁNEA de las demás pestañas cuando una activa el
 * SW nuevo (verificado en E2E 2026-07-13) — exactamente el "reload a mitad
 * de un formulario" que este diseño prohíbe. Con registro propio, las otras
 * pestañas se marcan vía `controllerchange` y recargan en su próxima
 * navegación o al click del toast.
 */
export function initAppUpdate(router: Router): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Aplicar update pendiente en el próximo cambio de ruta (momento seguro).
  // Se cancela la navegación SPA y se repite como full-load a la misma ruta
  // destino, ya con la versión nueva activa.
  router.beforeEach((to, from) => {
    if (!updateAvailable.value || applying) return true;
    if (from === START_LOCATION) return true; // navegación inicial de boot
    if (to.path === from.path) return true; // solo query/hash: no interrumpir
    if (reloadGuardActive()) return true; // anti-loop (TTL 2 min)
    void applyUpdate('route-change', to.fullPath);
    return false;
  });

  if (!('serviceWorker' in navigator)) return;

  // Multi-tab: si OTRA pestaña activó el SW nuevo, esta pestaña sigue con
  // código viejo pero el precache viejo ya se limpió → marcar update
  // pendiente (reload en próxima navegación o toast a los 30s).
  // El primer `controllerchange` de una instalación inicial (página que no
  // tenía controller) NO es un update — se ignora vía `hadController`.
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    if (applying) return;
    updateAvailable.value = true;
    scheduleToast(OTHER_TAB_TOAST_DELAY_MS);
  });

  // Solo en build de producción: en dev no hay sw.js (devOptions apagado) y
  // bajo vitest no hay que registrar nada.
  if (!import.meta.env.PROD) return;

  // Chequeo de consistencia post-aterrizaje: en la transición de SW hay una
  // race de ruteo (observada en E2E 2026-07-13) donde la navegación disparada
  // justo al activar puede ser servida por el SW SALIENTE — la página
  // aterriza UNA versión atrás, con sus chunks lazy ya purgados del precache.
  // Se detecta comparando el bundle que corre esta página contra el
  // index.html del precache; si difieren → un reload único (guard 30s) trae
  // la versión correcta. Corre en `load` Y en `pageshow` (restauraciones
  // BFCache no re-disparan `load`), independiente del register().
  window.addEventListener('load', () => void verifyLandedVersion());
  window.addEventListener('pageshow', () => void verifyLandedVersion());
  if (document.readyState === 'complete') void verifyLandedVersion();

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const markUpdateAvailable = () => {
          updateAvailable.value = true;
          scheduleToast(TOAST_DELAY_MS);
        };

        // SW nuevo ya esperando al abrir la página (deploy ocurrió antes de
        // esta carga, con el SW viejo aún controlando).
        if (registration.waiting && navigator.serviceWorker.controller) {
          markUpdateAvailable();
        }
        // SW nuevo detectado mientras la página está abierta. Si no hay
        // controller es la PRIMERA instalación (no un update): silencio.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              markUpdateAvailable();
            }
          });
        });

        // Chequeo periódico + al volver el foco/visibilidad a la pestaña.
        let lastCheck = Date.now();
        const check = () => {
          if (document.hidden) return;
          if (Date.now() - lastCheck < MIN_CHECK_GAP_MS) return;
          lastCheck = Date.now();
          // Offline o red caída: silencio, se reintenta en el próximo tick.
          void registration.update().catch(() => {});
        };
        window.setInterval(check, CHECK_INTERVAL_MS);
        // En iOS standalone `focus` es poco confiable; `visibilitychange`
        // es la señal principal al volver a la app.
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) check();
        });
        window.addEventListener('focus', check);
      })
      .catch(() => {
        // Registro falló (browser sin SW real, storage bloqueado): la app
        // funciona igual; el no-cache de Caddy garantiza HTML fresco.
      });
  });
}
